import map, { borg, MapObject } from "./map";
import schema from "./schema/index";
import parsePath from "./parsePath";
import { merge } from "lodash";
import perf from "./perf";
import { preFrameProcs } from "./animation";
import { createWebSocket, recordTapeMessage } from "./devtools/index";
import { retrySignIn } from "authSession";

let orphansByMissingParent = {};
let orphans = {};
let focusPath = "";
let filter = null;
let universeVersion = 1;
const inboundQueue = [];
const receiveLog = [];

let messagesReceived = 0;

perf.counter("messages", () => messagesReceived);
perf.counter("queue", () => inboundQueue.length);
perf.counter("objects", () => Object.keys(map.objects).length);
perf.counter("orphans", () => Object.keys(orphans).length);

function ReadUtf8Char(cursor) {
  const aBytes = cursor.arr;
  let nIdx = cursor.idx;
  const nLen = cursor.len;

  const nPart = aBytes[nIdx];
  const sView = String.fromCodePoint(
    nPart > 251 && nPart < 254 && nIdx + 5 < nLen /* six bytes */
      ? /* (nPart - 252 << 30) may be not so safe in ECMAScript! So...: */
        (nPart - 252) * 1073741824 +
          ((aBytes[++nIdx] - 128) << 24) +
          ((aBytes[++nIdx] - 128) << 18) +
          ((aBytes[++nIdx] - 128) << 12) +
          ((aBytes[++nIdx] - 128) << 6) +
          aBytes[++nIdx] -
          128
      : nPart > 247 && nPart < 252 && nIdx + 4 < nLen /* five bytes */
      ? ((nPart - 248) << 24) +
        ((aBytes[++nIdx] - 128) << 18) +
        ((aBytes[++nIdx] - 128) << 12) +
        ((aBytes[++nIdx] - 128) << 6) +
        aBytes[++nIdx] -
        128
      : nPart > 239 && nPart < 248 && nIdx + 3 < nLen /* four bytes */
      ? ((nPart - 240) << 18) +
        ((aBytes[++nIdx] - 128) << 12) +
        ((aBytes[++nIdx] - 128) << 6) +
        aBytes[++nIdx] -
        128
      : nPart > 223 && nPart < 240 && nIdx + 2 < nLen /* three bytes */
      ? ((nPart - 224) << 12) +
        ((aBytes[++nIdx] - 128) << 6) +
        aBytes[++nIdx] -
        128
      : nPart > 191 && nPart < 224 && nIdx + 1 < nLen /* two bytes */
      ? ((nPart - 192) << 6) + aBytes[++nIdx] - 128
      : /* nPart < 127 ? */ /* one byte */
        nPart
  );
  nIdx++;

  cursor.idx = nIdx;

  return sView;
}

function startWebsocket(
  statusUpdated,
  objectAdded,
  objectUpdated,
  objectDeleted,
  ui,
  initialCommand = null,
  otherCommandHandlers = {}
) {
  let webSocket = null;
  const historyLength = 256;
  const history = [];
  let historyIndex = 0;

  function adoptOrphan(o, parent) {
    if (o.id in map.objects) return;

    o.parent = parent;
    delete o["parentObjectRef"]; // not needed anymore
    map.objects[o.id] = o;
    parent.children.push(o);
    receiveLog.push("orphan adopted: " + o.id);
    objectAdded(o);
    delete orphans[o.id];

    const byparent = orphansByMissingParent[parent.id];
    if (byparent) {
      const idx = byparent.indexOf(o);
      if (idx != -1) {
        byparent.splice(idx, 1);
        if (byparent.length == 0) {
          delete orphansByMissingParent[parent.id];
        }
      }
    }
  }

  const processOne = () => {
    if (inboundQueue.length == 0) return;

    perf.begin("protocol");
    try {
      processOneCore();
    } finally {
      perf.end("protocol");
    }
  };

  const processOneCore = () => {
    messagesReceived++;

    const e = inboundQueue.shift();
    let edata = e.data;

    if (edata instanceof ArrayBuffer) {
      edata = new Uint8Array(edata);

      const bestI = edata[0];
      const snipLen = edata[1];
      const posInOther = edata[2];

      const other = history[bestI];

      const cursor = { arr: edata, idx: 3, len: edata.length };
      let result = "";
      while (edata[cursor.idx] != 0) {
        result += ReadUtf8Char(cursor);
      }
      cursor.idx++;
      for (let i = 0; i < snipLen; i++) {
        result += other[i + posInOther];
      }
      while (cursor.idx < edata.length) {
        result += ReadUtf8Char(cursor);
      }

      edata = result;
    }

    function IsSeparator(c) {
      return (
        c == "/" ||
        c == ":" ||
        c == "@" ||
        c == "#" ||
        c == "," ||
        c == "|" ||
        c == "\n"
      );
    }
    const maxParts = 16;
    let parts = [];
    let start = 0;
    let wasSep = false;
    let pc = 0;
    for (let i = 0; i < edata.length; i++) {
      const isSep = IsSeparator(edata[i]);
      if (!wasSep && isSep && i != 0) {
        parts.push(edata.substring(start, i));
        start = i;
        pc++;
        if (pc == maxParts) break;
      }
      wasSep = isSep;
    }
    parts.push(edata.substring(start));

    history[historyIndex] = parts;
    historyIndex = (historyIndex + 1) % historyLength;

    while (receiveLog.length > 10000) {
      receiveLog.shift();
    }
    receiveLog.push(edata.substring(0, 1000));

    recordTapeMessage(edata);

    execute(edata);
  };

  const execute = (edata) => {
    const d1 = edata.indexOf("@");
    const d2 = edata.indexOf(":");
    const type = d1 == -1 ? edata : edata.substring(0, d1);
    const path =
      d2 == -1 ? edata.substring(d1 + 1) : edata.substring(d1 + 1, d2);

    switch (type) {
      case "unauth":
        statusUpdated("AccessDenied");
        break;

      case "authok":
        map.objects[borg.id] = borg;
        objectAdded(borg);

        statusUpdated("Connected");

        if (initialCommand) {
          send(initialCommand);
        } else {
          send("statsinterval 5000");
          send("focus " + focusPath);
          send("compression low");

          if (filter && filter.collection) {
            setFilter(filter.collection, filter.values, true);
          } else {
            // Matches what the filter panel shows on load, and nothing more.
            //
            // This used to send a hand-written pattern that also excluded a list
            // of named apps and roles. Nothing in the UI reflected it, so those
            // apps could not be brought back and they never appeared among the
            // role icons — the panel would have been quietly lying about what it
            // was filtering. Anything genuinely too noisy to want by default is
            // now the role picker's and the activity filter's job.
            setFilter("envs", ["Live"], true);
          }
        }
        break;

      case "ipgeo":
        ui.handleIpGeoResponse(path, JSON.parse(edata.substring(d2 + 1)));
        break;

      case "statbucketupdates":
        const newStats = JSON.parse(edata.substring(d1 + 1));
        for (const objId in newStats) {
          if (!(objId in map.objects)) continue;
          const obj = map.objects[objId];
          const newStat = newStats[objId];
          merge(obj, { stats: newStat });

          const handlers = schema.objectTypes[obj.type].eventHandlers;
          if (!handlers) continue;

          const handler = handlers["statbucketupdates"];
          if (!handler) continue;

          handler(obj, newStat);
        }
        break;

      case "valuestats":
        console.error("not implemented: valuestats response");
        break;

      case "authfail":
        statusUpdated("Unauthenticated");
        // A token restored from a previous page load that the server no longer
        // accepts would otherwise leave the page sitting here indefinitely.
        // Discard it and sign in again.
        retrySignIn();
        break;

      case "delete":
        let deletedCt = 0;

        // remove any realised objects
        for (const objPath in map.objects) {
          if (objPath === path || objPath.startsWith(path + "/")) {
            const obj = map.objects[objPath];
            if (obj.parent) {
              obj.parent.children = obj.parent.children.filter(
                (o) => o !== obj
              );
            }

            receiveLog.push("deleted " + objPath);

            objectDeleted(obj);
            delete map.objects[objPath];
            deletedCt++;
          }
        }

        // remove any orphan objects
        for (const objPath in orphans) {
          if (objPath === path || objPath.startsWith(path + "/")) {
            const obj = orphans[objPath];

            receiveLog.push("deleting orphan " + objPath);

            delete orphans[objPath];
            deletedCt++;

            let byPArr = orphansByMissingParent[obj.parentObjectRef];
            if (byPArr) {
              const idx = byPArr.indexOf(obj);
              if (idx != -1) {
                byPArr.splice(idx, 1);
              } else {
                console.warn(
                  "orphan not found in parent's orphans array",
                  objPath
                );
              }
              if (byPArr.length == 0) {
                delete orphansByMissingParent[obj.parentObjectRef];
              }
            }
          }
        }

        if (path in map.pendingObjectData) {
          delete map.pendingObjectData[path];
          deletedCt++;
        }

        if (deletedCt == 0) {
          console.warn("nothing deleted for path", path);

          // diagnostic code to help fix this bug..
          /*receiveLog.filter(l => l.indexOf(path) != -1).forEach((l, i) =>
          {
            console.warn(i, l);
          });*/
        }

        break;

      case "remove":
        const remData = edata.substring(d2 + 1);

        const objPath = path + "/" + remData;
        delete map.pendingObjectData[objPath];

        // remove object from a collection
        if (objPath in map.objects) {
          const obj = map.objects[objPath];
          if (obj.parent) {
            obj.parent.children = obj.parent.children.filter((o) => o !== obj);
          }

          receiveLog.push("removing orphan " + objPath);

          objectDeleted(obj);
          delete map.objects[objPath];
        }

        if (objPath in orphans) {
          const obj = orphans[objPath];
          delete orphans[objPath];

          let byPArr = orphansByMissingParent[obj.parentObjectRef];
          if (byPArr) {
            const idx = byPArr.indexOf(obj);
            if (idx != -1) {
              byPArr.splice(idx, 1);
            } else {
              console.warn(
                "orphan not found in parent's orphans array",
                objPath
              );
            }
            if (byPArr.length == 0) {
              delete orphansByMissingParent[obj.parentObjectRef];
            }
          }
        }
        break;

      case "items":
      case "add":
        const collData = edata.substring(d2 + 1);
        if (collData == "") {
          // no items
          break;
        }
        const items = collData.split("\n");

        const collRoute = parsePath(path);

        if (collRoute == null) {
          console.warn("unknown collection: " + path);
          break;
        }

        const objectTypeName = collRoute.pop().collection.objectType;
        const objectType = schema.objectTypes[objectTypeName];

        const parentObjectRef =
          collRoute.length > 0
            ? collRoute.pop().objectRef
            : path.indexOf("/") == -1
            ? borg.id
            : null;

        if (parentObjectRef == null) {
          console.warn("no parent object ref for path: " + path);
          break;
        }

        let added = 0;

        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          const itemPath = path + "/" + item;

          if (itemPath in map.objects) {
            if (map.objects[itemPath].universeVersion != universeVersion) {
              // universe may have changed shape
              // (due to filter changing)
              // get the new shape of it...
              const subCollections = objectType.collections;
              for (const colName in subCollections) {
                if (colName == "*") continue;
                send("list " + itemPath + "/" + colName);
              }

              receiveLog.push(
                "just refreshing children for " + itemPath + " (exists)"
              );
            } else {
              receiveLog.push(
                "didn't add collection item " + itemPath + " (exists)"
              );
            }
            continue; // already exists
          }

          receiveLog.push("added collection item " + itemPath);

          let parentObj = map.objects[parentObjectRef];
          const newObj = new MapObject(itemPath, objectTypeName, parentObj);
          newObj.universeVersion = universeVersion;

          if (itemPath in map.pendingObjectData) {
            newObj.data = map.pendingObjectData[itemPath];
            delete map.pendingObjectData[itemPath];
          }

          // get: data
          if (objectType.hasData && !newObj.data) {
            send("get " + itemPath);
            receiveLog.push("requesting data for " + itemPath);
          }

          // get: sub-items
          const subCollections = objectType.collections;
          for (const colName in subCollections) {
            if (colName == "*") continue;
            send("list " + itemPath + "/" + colName);
          }

          // get: stats
          const statPaths = [];
          const statTypes = objectType.stats || {};
          for (const statType in statTypes) {
            const stats = objectType.stats[statType];
            // iterate stats array
            for (let j = 0; j < stats.length; j++) {
              const stat = stats[j];
              statPaths.push(statType + "stats/" + stat);
            }
          }
          if (statPaths.length > 0) {
            send("getstats " + itemPath + " " + statPaths.join(","));
          }

          // new for filter:
          // keep objects as orphans until a descendant matches the filter
          const matchesFilter =
            !filter ||
            filter.values.some(
              (v) => newObj.id.indexOf(filter.collection + "/" + v) != -1
            );
          var orphan = !parentObj || !matchesFilter;

          if (orphan) {
            if (!(parentObjectRef in orphansByMissingParent)) {
              orphansByMissingParent[parentObjectRef] = [];
            }
            newObj.parentObjectRef = parentObjectRef;
            receiveLog.push("adding orphan - " + newObj.id);
            orphansByMissingParent[parentObjectRef].push(newObj);
            orphans[newObj.id] = newObj;
          } else {
            added++;
            map.objects[itemPath] = newObj;

            const orphansByP = orphansByMissingParent[itemPath];
            if (orphansByP) {
              for (let j = 0; j < orphansByP.length; j++) {
                adoptOrphan(orphansByP[j], newObj);
                added++;
              }
              delete orphansByMissingParent[parentObjectRef];
            }

            parentObj = map.objects[parentObjectRef];
            if (parentObj) {
              parentObj.children.push(newObj);
              objectAdded(newObj);
            }
          }

          if (matchesFilter) {
            const parents = [newObj];
            let p = newObj;
            let r;
            while (true) {
              p =
                p.parent ||
                ((r = p.parentObjectRef) && (map.objects[r] || orphans[r]));
              if (!p) break;
              parents.push(p);
            }
            if (parents.length) {
              let lastP = parents.pop();
              if (lastP == borg) {
                while (parents.length > 0) {
                  p = parents.pop();
                  adoptOrphan(p, lastP);
                  lastP = p;
                }
              }
            }
          }
        }
        break;

      case "data":
        const dataData = edata.substring(d2 + 1);
        if (path in map.objects) {
          receiveLog.push("got data for " + path + " (live)");
          map.objects[path].data = dataData;
          objectUpdated(map.objects[path]);
        } else if (path in orphans) {
          receiveLog.push("got data for " + path + " (orphan)");
          orphans[path].data = dataData;
        } else {
          receiveLog.push("got data for " + path + " (pending)");
          map.pendingObjectData[path] = dataData;
        }
        break;

      case "event":
        const d3 = edata.indexOf("|");
        const evtKind = edata.substring(d2 + 1, d3);
        const evtData = edata.substring(d3 + 1);

        const route = parsePath(path);
        if (route == null) {
          console.warn("Unknown object: " + path);
          break;
        }

        const objectInfo = route.pop();

        // could be an orphaned object, or just something we don't know about..
        if (!(objectInfo.objectRef in map.objects)) break;

        const handlers = objectInfo.objectType.eventHandlers;
        if (!handlers) break;

        const handler = handlers[evtKind];
        if (!handler) break;

        handler(map.objects[objectInfo.objectRef], evtData, ui);
        break;

      case "response":
        const d3r = edata.indexOf("|");
        const evtKindr = edata.substring(d2 + 1, d3r);
        const evtDatar = edata.substring(d3r + 1);

        const router = parsePath(path);
        if (router == null) {
          console.warn("Unknown object: " + path);
          break;
        }

        const objectInfor = router.pop();

        if (!(objectInfor.objectRef in map.objects)) return;

        const handlersr = objectInfor.objectType.responseHandlers;
        if (!handlersr) break;

        const handlerr = handlersr[evtKindr];
        if (!handlerr) break;

        handlerr(map.objects[objectInfor.objectRef], evtDatar, ui, path, send);
        break;

      default:
        if (type in otherCommandHandlers) {
          otherCommandHandlers[type](edata.substring(d1 + 1));
        } else {
          console.warn("Unknown command: " + type);
        }
    }
  };

  // `value` may be a single value or an array of them, so the filter panel can
  // leave more than one environment showing. An empty array means nothing in that
  // collection is wanted; null means the collection is not filtered at all.
  const setFilter = (collection, value, go, override) => {
    const values =
      value == null ? null : Array.isArray(value) ? value.slice() : [value];

    if (collection == null || values == null) {
      filter = null;
    } else {
      filter = { collection, values };
    }

    if (filter != null) {
      const toKeep = {};

      const keep = (o) => {
        toKeep[o.id] = true;
        if (o.parent) keep(o.parent);

        // keep any children of type 'externalResource'
        if (o.children) {
          for (let i = 0; i < o.children.length; i++) {
            const c = o.children[i];
            if (c.type == "externalResource") {
              toKeep[c.id] = true;
            }
          }
        }
      };

      for (const objPath in map.objects) {
        try {
          let matches = objPath == "*";
          for (let i = 0; i < values.length && !matches; i++) {
            matches = objPath.indexOf(collection + "/" + values[i]) != -1;
          }
          if (matches) keep(map.objects[objPath]);
        } catch (e) {
          console.error(e);
        }
      }

      for (const objPath in map.objects) {
        if (objPath in toKeep) continue;

        const obj = map.objects[objPath];
        if (obj.parent) {
          obj.parent.children = obj.parent.children.filter((o) => o !== obj);
        }
        objectDeleted(map.objects[objPath]);
        delete map.pendingObjectData[objPath];
        delete map.objects[objPath];
      }
    }

    // this will cause a complete re-query from the root when the result from "list apps" etc. come in
    universeVersion++;

    if (override) {
      send(override);
    } else {
      if (filter) {
        send(
          "filter out \\/" +
            filter.collection +
            "\\/(?!" +
            (filter.values.length === 1
              ? filter.values[0]
              : "(" + filter.values.join("|") + ")") +
            ")"
        );
      } else {
        send("filter in "); // no filter
      }
    }

    if (go) send("go");

    // in case we need more objects..
    for (const colName in schema.collections) {
      send("list " + colName);
    }
  };

  const setFocus = (p, includeParents) => {
    const oldPath = focusPath;
    focusPath = p;

    for (const objPath in map.objects) {
      try {
        if (
          objPath.indexOf(p) == -1 &&
          p.indexOf(objPath) == -1 &&
          objPath != "*"
        ) {
          if (includeParents) {
            // it's a candidate for deletion. double-check it's not an external resource
            // of something that should stay, however
            const eri = objPath.indexOf("/externalResources/");
            if (eri != -1) {
              const parentPath = objPath.substring(0, eri);
              if (parentPath.indexOf(p) != -1 || p.indexOf(parentPath) != -1) {
                continue;
              }
            }
            if (objPath.startsWith("externalResources/")) {
              continue;
            }
          }

          const obj = map.objects[objPath];
          if (obj.parent) {
            obj.parent.children = obj.parent.children.filter((o) => o !== obj);
          }

          objectDeleted(map.objects[objPath]);
          delete map.pendingObjectData[objPath];
          delete map.objects[objPath];
        }
      } catch (e) {
        console.error(e);
      }
    }

    send("focus " + focusPath);

    const route = parsePath(focusPath);
    if (route == null || includeParents) {
      for (const colName in schema.collections) {
        send("list " + colName);
      }
    }
    if (route != null) {
      for (let i = 0; i < route.length; i++) {
        const { collectionRef, objectRef } = route[i];
        if (collectionRef) {
          send("list " + collectionRef);
        }
        if (objectRef && includeParents) {
          send("list " + objectRef + "/externalResources");
        }
      }
    }
  };

  const send = (d) => {
    webSocket.send(d);
  };

  const onmessage = (e) => {
    inboundQueue.push(e);
  };

  // Processing used to happen inline on the socket callback, so message handling
  // interleaved with rendering at arbitrary points in the frame and had no
  // budget. Draining once per frame keeps it in one place and lets a batch of
  // adds share a single layout solve.
  //
  // The budget bounds real time, not the animation clock, so capture mode - where
  // the clock advances by a fixed step regardless of how long the frame took -
  // drains everything to keep recordings deterministic.
  const DRAIN_BUDGET_MS = 6;

  preFrameProcs.drainInbound = () => {
    const unbounded = !!window.captureOption;
    const started = performance.now();

    while (inboundQueue.length) {
      try {
        processOne();
      } catch (err) {
        // Matches the old behaviour of an exception escaping the socket callback:
        // that message is abandoned, the rest are unaffected.
        console.error(err);
      }
      if (!unbounded && performance.now() - started >= DRAIN_BUDGET_MS) break;
    }
  };

  let reconnectTimer = null;

  const onclose = (e) => {
    for (const objPath in map.objects) {
      try {
        objectDeleted(map.objects[objPath]);
        delete map.pendingObjectData[objPath];
        delete map.objects[objPath];
      } catch {}
    }
    orphansByMissingParent = {};
    borg.children = [];

    statusUpdated("Disconnected");
    if (reconnectTimer == null) {
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        open();
      }, 3000);
    }
  };

  const onopen = (e) => {
    statusUpdated("Authenticating");
    send("auth " + window.auth);
  };

  function open() {
    statusUpdated("Connecting");
    history.length = 0; // reset compression history buffer
    historyIndex = 0;
    webSocket = createWebSocket(
      process.env.LOCAL_BORG_SERVER
        ? "ws://localhost:5001/api/hose"
        : "wss://borg.red-river.app/api/hose"
    );
    webSocket.binaryType = "arraybuffer";
    webSocket.onmessage = onmessage;
    webSocket.onclose = onclose;
    webSocket.onerror = onclose;
    webSocket.onopen = onopen;
  }

  open();

  return {
    sendCommand: (c) => send(c),
    setFocus: (p, ip) => setFocus(p, ip),
    setFilter: (c, v) => setFilter(c, v),
  };
}

export { startWebsocket, receiveLog };
